// @flow
import ky from 'ky';
import Ajv from 'ajv';
import invariant from 'invariant';
import BluebirdPromise from 'bluebird';
import noop from 'lodash/fp/noop';

import type { Resource } from './resources';
import { HTTPError } from './http-error';

import type { Dispatch, GetState } from '~/store';
import {
  requestResource,
  receiveResource,
  failToReceiveResource,
} from 'actions/resource-actions';

BluebirdPromise.config({
  cancellation: true,
});

export class JSONValidationError extends Error {
  constructor(resourceName: string, errorString: string) {
    super(
      `JSON validation of response for resource ${resourceName} failed!
  ${errorString}`,
    );
    // eslint-disable-next-line fp/no-mutation
    this.name = 'JSONValidationError';
  }
}

export type Method = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'HEAD' | 'PATCH';

export type HTTPParams = { +[string]: any };

type HTTPRequest = {
  url: string,
  method?: Method,
  params?: HTTPParams,
};

const ajv = new Ajv();

export const rangeString = ({
  start,
  end,
  count,
}: {
  start: number,
  end?: number,
  count?: number,
}): string => {
  if (typeof end === 'number') return `${start}-${end}`;

  invariant(
    typeof count === 'number',
    'either end or count must be given to rangeString',
  );

  return `${start}-${start + (count - 1)}`;
};

const validationErrorForJsonGivenSchema = (
  json: Object,
  schema: Object,
  name: string = 'JSON',
): JSONValidationError | null => {
  try {
    return !ajv.validate(schema, json)
      ? new JSONValidationError(name, ajv.errorsText())
      : null;
  } catch (e) {
    return e;
  }
};

const validationErrorForResponse = <T, S>(
  data: any,
  resource: Resource<T, S>,
): JSONValidationError | null =>
  resource.schema
    ? validationErrorForJsonGivenSchema(data, resource.schema, resource.name)
    : null;

const kyRequesterForMethod = (method: Method) =>
  ({
    HEAD: ky.head,
    GET: ky.get,
    POST: ky.post,
    PUT: ky.put,
    PATCH: ky.patch,
    DELETE: ky.delete,
  }[method]);

export const jsonForRequest = ({
  url,
  method = 'GET',
  params,
}: HTTPRequest): BluebirdPromise<any> =>
  new BluebirdPromise((resolve, reject, onCancel) => {
    const controller = new AbortController();
    const { signal } = controller;

    if (onCancel) {
      onCancel(() => {
        controller.abort();
      });
    }

    return kyRequesterForMethod(method)(url, {
      credentials: 'include',
      timeout: false,
      headers: {
        accept: 'application/javascript, application/json',
        'accept-language': 'en-US,en;q=0.9,es;q=0.8,en-AU;q=0.7,ja;q=0.6',
        'cache-control': 'no-cache',
        'content-type': 'application/json',
      },
      signal,
      ...(['PUT', 'POST', 'PATCH', 'HEAD'].includes(method)
        ? { json: params }
        : { ...null }),
    })
      .then((response) => {
        if (response.status > 399) {
          reject(response);
        } else {
          response
            .json()
            .then((body) => {
              if (!response.headers.has('content-range')) {
                resolve(body);
              } else {
                const range = response.headers.get('content-range');

                resolve({
                  ...(Array.isArray(body) ? { body } : body),
                  range,
                });
              }
            })
            .catch(reject);
        }
      })
      .catch((e) => reject(e));
  });

const urlForResource = <T, S>(
  apiUrl: string,
  resource: Resource<T, S>,
  resourceId: string | null,
): string =>
  [
    apiUrl,
    typeof resource.path === 'function'
      ? resource.path(resourceId || '')
      : resource.path,
  ].join('');

const httpRequestForResource = <T, S>({
  apiUrl,
  method = 'GET',
  resource,
  resourceId,
  params = { ...null },
}: {
  apiUrl: string,
  method?: Method,
  resource: Resource<T, S>,
  resourceId: null | string,
  params: HTTPParams,
}): HTTPRequest => ({
  url: urlForResource(apiUrl, resource, resourceId),
  method,
  params,
});

export const jsonForResourceRequest = <T, S>({
  apiUrl,
  method = 'GET',
  resource,
  resourceId,
  params = { ...null },
}: {
  apiUrl: string,
  method?: Method,
  resource: Resource<T, S>,
  resourceId: string | null,
  params?: HTTPParams,
}): BluebirdPromise<T> =>
  new BluebirdPromise((resolve, reject, onCancel) => {
    const promise = jsonForRequest(
      httpRequestForResource({
        apiUrl,
        method,
        resource,
        resourceId,
        params,
      }),
    )
      .then((json) => {
        const error = validationErrorForResponse(json, resource);

        if (error) {
          // eslint-disable-next-line no-console
          console.error(error);
          reject(error);
        } else {
          resolve(resource.deserialized(json));
        }
      })
      .catch(reject);

    if (onCancel) {
      onCancel(() => {
        promise.cancel();
      });
    }
  });

const resourceRequestThunk =
  (method: Method = 'GET') =>
  <T, S>(
    resource: Resource<T, S>,
    resourceId: string | null = null,
    params: {||} = { ...null },
    dispatchUpdatesOnResourceRecieved: boolean = true,
  ) =>
  (dispatch: Dispatch, getState: GetState): BluebirdPromise<any> => {
    dispatch(requestResource(resource.name, method, resourceId));

    /* 💁 the api url passed from scramble4 ends in a slash */
    const state = getState();
    const apiUrl = state.apiUrl || (state: any).present?.apiUrl;

    invariant(apiUrl, 'apiUrl must be given before any API calls');

    return new BluebirdPromise((resolve, reject) =>
      jsonForResourceRequest({
        apiUrl,
        method,
        resource,
        resourceId,
        params,
      })
        .then((data) => {
          if (dispatchUpdatesOnResourceRecieved) {
            dispatch(receiveResource(resource.name, method, resourceId, data));
          }
          return resolve(data);
        })
        // eslint-disable-next-line consistent-return
        .catch((httpError: Error) => {
          if (httpError instanceof HTTPError) {
            dispatch(
              failToReceiveResource(
                resource.name,
                method,
                resourceId,
                httpError.response
                  ? httpError.response.statusText
                  : httpError.message,
              ),
            );

            // i'm not sure we actually want to reject in any of the following
            // scenarios. should we just rely on the reducers handling the failure
            // cases from here? is there a way to actually catch these rejections?

            // is there actually a scenario in which httpError.response or
            // httpError.response.json don't exist? json is just a function that's
            // always on the response object, so checking for it here doesn't do
            // much.
            // $FlowFixMe[method-unbinding]
            if (httpError.response && httpError.response.json) {
              return httpError.response
                .json()
                .then((ejson) => {
                  reject(ejson);
                })
                .catch(() => {
                  // this basically always happens when there's a non-json error
                  // response (most of the time, e.g. 404), so don't bother
                  // throwing the json parse error
                  reject(httpError);
                });
            }
          } else {
            dispatch(
              failToReceiveResource(
                resource.name,
                method,
                resourceId,
                httpError.message,
              ),
            );
            return reject(httpError);
          }
          reject(httpError);
        }),
    );
  };

// $FlowFixMe[signature-verification-failure]
export const get = resourceRequestThunk('GET');
// $FlowFixMe[signature-verification-failure]
export const put = resourceRequestThunk('PUT');
// $FlowFixMe[signature-verification-failure]
export const post = resourceRequestThunk('POST');
// $FlowFixMe[signature-verification-failure]
export const del = resourceRequestThunk('DELETE'); // didn't want to use javascript keyword `delete`

export const asBluebirdPromise = <T>(promise: Promise<T>): BluebirdPromise<T> =>
  new BluebirdPromise((resolve, reject) => promise.then(resolve).catch(reject));

/**
 * Takes a standard paged response fetcher and returns a promise for the entire resultset of rows.
 *
 * Specify `onProgress` to peek into the total and completed rows. `total` is the expected total
 * number of result rows across all pages, and `completed` is the number of rows that have already
 * been fetched.
 */
export const fullResultsOfPagedQuery = <
  Row,
  Response: $ReadOnly<{ +results: $ReadOnlyArray<Row>, +total: number }>,
>(
  fetcher: (start: number, count: number) => BluebirdPromise<Response>,
  pageSize: number,
  onProgress?: (total: number, completed: number, response: Response) => void,
): BluebirdPromise<$ReadOnlyArray<Row>> => {
  // this is implemented as a recursive promise chain to eliminate any local state
  const step = (rowsSoFar: $ReadOnlyArray<Row> = []) =>
    // we're deriving the starting point from rowsSoFar
    fetcher(rowsSoFar.length, pageSize).then((response) => {
      // our full resultset including the results from this pass
      const rows = [...rowsSoFar, ...response.results];

      if (onProgress) onProgress(response.total, rows.length, response);

      // are there still more rows to fetch?
      if (rowsSoFar.length + pageSize < response.total) {
        // fetch more rows, passing along our resultset to be appended onto in later steps
        return step(rows);
      }

      // we're done, our resultset is complete so we only have to return it
      return rows;
    });

  // kick off our recursive chain
  return step();
};

/**
 * Takes a fetcher builder that takes a chunk and returns a standard page fetcher. Also takes
 * a list of chunks. The chunks will be fed into the fetcher builder, and the fetcher will be
 * used to fetch all pages sequentially for the given chunk.
 *
 * Specify `onProgress` to peek into the total and completed chunks. `total` is the total
 * number of chunks passed, and `completed` is the number of chunks that have already
 * been fetched. Note that `completed` in this case can be a float, as we may have internal
 * information about progress within a chunk.
 */
export const chunkedFullResultsOfPagedQuery = <
  Row,
  Response: $ReadOnly<{ +results: $ReadOnlyArray<Row>, +total: number }>,
  Chunk,
>(
  fetcherBuilder: (Chunk) => (
    start: number,
    count: number,
  ) => BluebirdPromise<Response>,
  chunks: $ReadOnlyArray<Chunk>,
  pageSize: number,
  onProgress?: (
    total: number,
    completed: number,
    response: Response,
  ) => void = noop,
): BluebirdPromise<$ReadOnlyArray<Row>> => {
  const step = (
    chunksRemaining: $ReadOnlyArray<Chunk>,
    rowsSoFar: $ReadOnlyArray<Row>,
  ) => {
    const currentChunk = chunksRemaining[0];
    return fullResultsOfPagedQuery(
      fetcherBuilder(currentChunk),
      pageSize,
      (totalRows, completedRows, response) =>
        onProgress(
          chunks.length,
          chunks.length - chunksRemaining.length + completedRows / totalRows,
          response,
        ),
    ).then((chunkRows) => {
      const nextChunks = chunksRemaining.slice(1);
      const newRowsSoFar = [...rowsSoFar, ...chunkRows];
      if (nextChunks.length === 0) {
        return newRowsSoFar;
      }

      return step(nextChunks, newRowsSoFar);
    });
  };

  // kick off our recursive chain
  return step(chunks, []);
};

export const startBrowserDownload: (url: string) => void = (url) => {
  // eslint-disable-next-line no-underscore-dangle
  if (global.__mockStartBrowserDownload) {
    // eslint-disable-next-line no-underscore-dangle
    global.__mockStartBrowserDownload(url);
    return;
  }

  window.open(url, '_blank');
};
